import { sortBy } from 'lodash-es';
import moment from 'moment';
import React from 'react';
import { connect } from 'react-redux';
import { Button, Card, Label, Table } from 'semantic-ui-react';
import { jobsStateType } from '../../../../types';
import { jobsActions } from '../../../../behavior/jobs';
import Loading from '../../../Loading';

const NextTrigger = ({ triggers }) => {
  if (triggers && triggers.length > 0) {
  	//different date format is provided by different applications
    let next = moment.unix(triggers[0].nextFireTime);
    if (!next.isValid()) next = moment(triggers[0].nextFireTime);
    return <>{next.toNow(true)}</>;
  }
  return <i>Not scheduled</i>;
};

const JobInstance = ({ instance }) =>
  instance ? (
    <>
      {instance.endTime
        ? `Ended ${moment(instance.endTime).fromNow()} by `
        : `Running for ${moment(instance.startTime).fromNow(true)} by `}
      <b>{instance.triggeredBy || 'system'}</b>
    </>
  ) : (
    <i>none</i>
  );

const JobCounters = ({ counters }) => {
  const { NORMAL, PAUSED, ERROR, BLOCKED, COMPLETE } = counters;
  return (
    <>
      {NORMAL ? <Label color="blue">{NORMAL}</Label> : null}
      {PAUSED ? <Label color="yellow">{PAUSED}</Label> : null}
      {ERROR ? <Label color="red">{ERROR}</Label> : null}
      {BLOCKED ? <Label color="purple">{BLOCKED}</Label> : null}
      {COMPLETE ? <Label color="green">{COMPLETE}</Label> : null}
      {!(NORMAL || PAUSED || ERROR || BLOCKED || COMPLETE) ? <i>no trigger</i> : null}
    </>
  );
};

const rowKey = ({ group, name }) => `${group}.${name}`;

const headerRow = {
  textAlign: 'center',
  cells: [
    {
      key: 'group',
      content: 'Group'
    },
    {
      key: 'name',
      content: 'Name'
    },
    {
      key: 'desc',
      content: 'Description'
    },
    {
      key: 'last-instance',
      content: 'Last Instance'
    },
    {
      key: 'new-instance',
      content: 'Next Instance'
    },
    {
      key: 'states',
      content: 'States'
    },
    {
      key: 'actions',
      content: 'Actions'
    }
  ]
};

const renderError = ({ response }) => (
  <Card>
    <Card.Content>{`${response.status} - ${response.statusText}`}</Card.Content>
  </Card>
);

const handleAction = (axiosConfig, keycloakConfig, action, jobKey) => e => {
  e.stopPropagation();
  action(axiosConfig, keycloakConfig, jobKey);
};

const renderRow = ({ getJob, startJob, pauseJob, resumeJob }, axiosConfig, keycloakConfig) => ({
  jobKey,
  description,
  lastInstance,
  stateCounters,
  triggers
}) => ({
  key: rowKey(jobKey),
  textAlign: 'center',
  onClick: () => getJob(axiosConfig, keycloakConfig, jobKey),
  cells: [
    {
      content: jobKey.group,
      collapsing: true,
      textAlign: 'left',
      key: 'job-group'
    },
    {
      content: jobKey.name,
      collapsing: true,
      textAlign: 'left',
      key: 'job-name'
    },
    { content: description, key: 'job-desc' },
    {
      content: lastInstance ? <JobInstance instance={{ ...lastInstance }} /> : null,
      key: 'job-last-instance'
    },
    {
      content: <NextTrigger triggers={triggers} />,
      key: 'job-triggers'
    },
    { content: <JobCounters counters={{ ...stateCounters }} />, key: 'job-counters' },
    {
      content: (
        <>
          <Button
            basic
            size="mini"
            title="Trigger now"
            icon="step forward"
            onClick={handleAction(axiosConfig, keycloakConfig, startJob, jobKey)}
          />
          {stateCounters.NORMAL > 0 ? (
            <Button
              title="Pause all triggers"
              inverted
              size="mini"
              color="orange"
              icon="pause"
              onClick={handleAction(axiosConfig, keycloakConfig, pauseJob, jobKey)}
            />
          ) : null}
          {stateCounters.PAUSED > 0 ? (
            <Button
              title="Resume all triggers"
              inverted
              size="mini"
              color="green"
              icon="play circle"
              onClick={handleAction(axiosConfig, keycloakConfig, resumeJob, jobKey)}
            />
          ) : null}
        </>
      ),
      key: 'job-buttons'
    }
  ]
});

const JobTable = ({ data, isLoading, actions, errors, axiosConfig, keycloakConfig }) => (
  <Card fluid raised>
    <Loading isLoading={isLoading.jobs} />
    <Card.Content style={{ minHeight: '510px' }}>
      {errors.jobs ? (
        renderError(errors.jobs)
      ) : (
        <Table
          selectable
          width={16}
          renderBodyRow={renderRow(actions, axiosConfig, keycloakConfig)}
          tableData={sortBy(data.jobs, ['jobKey.group', 'jobKey.name'])}
          headerRow={headerRow}
        />
      )}
    </Card.Content>
  </Card>
);

JobTable.propTypes = {
  ...jobsStateType.isRequired
};

const mapStateToProps = state => ({
  ...state.jobs
});

const mapDispatchToProps = dispatch => ({
  actions: {
    getJob: (axiosConfig, keycloakConfig, jobKey) => dispatch(jobsActions.get(axiosConfig, keycloakConfig, jobKey)),
    startJob: (axiosConfig, keycloakConfig, jobKey) => dispatch(jobsActions.trigger(axiosConfig, keycloakConfig, jobKey)),
    resumeJob: (axiosConfig, keycloakConfig, jobKey) => dispatch(jobsActions.resume(axiosConfig, keycloakConfig, jobKey)),
    pauseJob: (axiosConfig, keycloakConfig, jobKey) => dispatch(jobsActions.stop(axiosConfig, keycloakConfig, jobKey))
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(JobTable);
